import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/authcontext';
import { useNavigate, useLocation } from 'react-router-dom';
import io from 'socket.io-client';
import { toast } from 'react-toastify';
import api from '../api/axios';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Avatar,
  Chip,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  Videocam,
  Phone,
  PhoneDisabled,
  Person,
  Close
} from '@mui/icons-material';

// Was hardcoded to http://localhost:3000, so incoming calls could never work
// anywhere but this machine — including in production. See audit #35.
const SOCKET_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';

// Pages where an incoming-call dialog would be wrong or disruptive.
// '/graph-login' is listed explicitly: '/graph-login'.includes('/login') is
// false, because the string contains '-login', not '/login'.
const EXCLUDED_PATHS = [
  '/video-chat',
  '/login',
  '/signup',
  '/doctorLogin',
  '/graph-login',
  '/forgot-password',
  '/reset-password'
];

// Single global socket shared across mounts, so navigating between pages does
// not tear down and rebuild the connection.
let globalSocket = null;
let currentUserId = null;

// Any other component (e.g. Messages.jsx) that needs the same socket
// subscribes here rather than opening its own connection. Two independent
// sockets for the same user would each call registerConnection on the
// backend, which is keyed by role:id alone — the second one to connect wins,
// and the other becomes silently unreachable via emitToUser. That's the exact
// "last one wins" bug class this app already fixed once elsewhere (bare id
// collisions across roles); this avoids reintroducing the same shape of bug
// via a duplicate connection instead of a duplicate id.
const socketReadyListeners = new Set();

export function onGlobalSocketReady(callback) {
  if (globalSocket && globalSocket.connected) {
    callback(globalSocket);
  }
  socketReadyListeners.add(callback);
  return () => socketReadyListeners.delete(callback);
}

const GlobalIncomingCallHandler = () => {
  const [socket, setSocket] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [isResponding, setIsResponding] = useState(false);
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const ringtoneRef = useRef(null);
  const mountedRef = useRef(true);

  // Mirrors incomingCall so callbacks can read the current value without being
  // recreated. The ringtone previously closed over a stale null and stopped
  // immediately, so it never actually rang.
  const incomingCallRef = useRef(null);

  const shouldHandleCalls = useCallback(() => {
    const pathname = location.pathname;
    if (!isAuthenticated || !user) return false;
    return !EXCLUDED_PATHS.some(p => pathname.toLowerCase().startsWith(p.toLowerCase()));
  }, [location.pathname, isAuthenticated, user]);

  const stopRingtone = useCallback(() => {
    if (!ringtoneRef.current) return;
    try {
      if (ringtoneRef.current.interval) clearInterval(ringtoneRef.current.interval);
      if (ringtoneRef.current.ctx && ringtoneRef.current.ctx.state !== 'closed') {
        ringtoneRef.current.ctx.close();
      }
    } catch (error) {
      // Nothing useful to do if teardown fails.
    }
    ringtoneRef.current = null;
  }, []);

  // Generates a two-tone ring with the Web Audio API. The previous version used
  // a base64 WAV containing no audio samples, so it was silent even when it did
  // fire. Browsers block audio before any user interaction — the catch below
  // keeps that from throwing; the dialog is still shown either way.
  const playRingtone = useCallback(() => {
    stopRingtone();

    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;

      const ctx = new AudioCtx();

      const beep = () => {
        if (!mountedRef.current || !incomingCallRef.current) return;
        if (ctx.state === 'closed') return;

        [0, 0.4].forEach((offset) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.value = offset === 0 ? 660 : 550;
          gain.gain.setValueAtTime(0.0001, ctx.currentTime + offset);
          gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + offset + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + offset + 0.32);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start(ctx.currentTime + offset);
          osc.stop(ctx.currentTime + offset + 0.35);
        });
      };

      beep();
      const interval = setInterval(beep, 2500);
      ringtoneRef.current = { ctx, interval };
    } catch (error) {
      // Autoplay blocked, or Web Audio unavailable. The visual dialog still shows.
    }
  }, [stopRingtone]);

  const handleCallEnd = useCallback((message) => {
    incomingCallRef.current = null;
    setIncomingCall(null);
    setIsResponding(false);
    stopRingtone();
    toast.dismiss();

    if (message) toast.info(message);
  }, [stopRingtone]);

  // Uses the axios instance rather than a relative fetch. The relative version
  // went through CRA's dev proxy, which rewrote the Origin header and tripped
  // the CORS check — and would have 404'd against the static site in production.
  // userId and role are no longer sent: the server reads both from the token.
  const updateUserStatus = useCallback(async (status, socketId = null) => {
    if (!mountedRef.current) return;

    try {
      await api.post('/video-call/update-status', { status, socketId });
    } catch (error) {
      console.error(`[CallHandler] Status update failed: ${error.message}`);
    }
  }, []);

  const connectSocket = useCallback(async () => {
    if (!shouldHandleCalls() || !mountedRef.current) return null;

    if (globalSocket && currentUserId === user.id && globalSocket.connected) {
      setSocket(globalSocket);
      return globalSocket;
    }

    if (globalSocket && currentUserId !== user.id) {
      try {
        globalSocket.off();
        if (globalSocket.connected) globalSocket.disconnect();
      } catch (error) { /* already gone */ }
      globalSocket = null;
      currentUserId = null;
    }

    const token = localStorage.getItem('token');
    if (!token) return null;

    try {
      // Identity comes from the signed token. The handshake no longer sends
      // userId/userName/userRole — the server ignores them, and passing
      // client-supplied identity alongside a token invites confusion about
      // which one is authoritative.
      const newSocket = io(SOCKET_URL, {
        transports: ['websocket', 'polling'],
        auth: { token },
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000
      });

      globalSocket = newSocket;
      currentUserId = user.id;

      newSocket.on('connect', () => {
        if (!mountedRef.current) return;
        setSocket(newSocket);
        updateUserStatus('online', newSocket.id);
        // Anything waiting on the shared socket (e.g. Messages.jsx) gets it now.
        socketReadyListeners.forEach((cb) => {
          try { cb(newSocket); } catch (error) { /* one bad listener shouldn't break the rest */ }
        });
      });

      newSocket.on('disconnect', (reason) => {
        if (!mountedRef.current) return;
        if (reason !== 'io client disconnect') updateUserStatus('offline');
      });

      newSocket.on('connect_error', (error) => {
        console.error(`[CallHandler] Connection error: ${error.message}`);
      });

      newSocket.on('incoming-call', (callData) => {
        if (!mountedRef.current || !shouldHandleCalls()) return;

        incomingCallRef.current = callData;
        setIncomingCall(callData);
        playRingtone();

        toast.info(`Incoming video call from ${callData.caller?.name || 'someone'}`, {
          toastId: `incoming-call-${callData.callId}`,
          autoClose: false,
          closeOnClick: false,
          draggable: false
        });
      });

      newSocket.on('call-accepted', (data) => {
        if (!mountedRef.current) return;
        handleCallEnd();
        navigate(`/video-chat/${data.roomId}`);
      });

      newSocket.on('call-rejected', () => {
        if (!mountedRef.current) return;
        handleCallEnd('Call was declined');
      });

      newSocket.on('call-ended', (data) => {
        if (!mountedRef.current) return;
        // Server sends endedBy, not reason.
        handleCallEnd(data?.endedBy ? `Call ended by ${data.endedBy}` : 'Call ended');
      });

      // The server emits call-error when the response cannot be processed:
      // the call is no longer pending, the caller could not be reached, or the
      // payload was invalid. There was no listener for it, so the dialog spun
      // forever on a failure the server had already reported.
      newSocket.on('call-error', (data) => {
        if (!mountedRef.current) return;
        console.error(`[CallHandler] Server rejected the call response: ${data?.error}`);
        handleCallEnd(data?.error || 'Could not connect the call. Please try again.');
      });

      newSocket.on('call-timeout', () => {
        if (!mountedRef.current) return;
        handleCallEnd('Call timed out');
      });

      return newSocket;
    } catch (error) {
      console.error(`[CallHandler] Socket creation failed: ${error.message}`);
      return null;
    }
  }, [user, navigate, updateUserStatus, handleCallEnd, playRingtone, shouldHandleCalls]);

  const acceptCall = useCallback(async () => {
    if (!socket || !incomingCall || isResponding) return;

    setIsResponding(true);
    stopRingtone();
    toast.dismiss();

    try {
      socket.emit('respond-to-call', {
        callId: incomingCall.callId,
        response: 'accepted'
      });

      // Safety net. If neither call-accepted nor call-error arrives — server
      // restarted mid-call, socket dropped silently — stop spinning rather
      // than leaving the user stuck on a dialog they cannot dismiss.
      setTimeout(() => {
        if (mountedRef.current && incomingCallRef.current) {
          handleCallEnd('Could not connect. Please try again.');
        }
      }, 10000);
    } catch (error) {
      toast.error('Failed to accept call');
      setIsResponding(false);
    }
  }, [socket, incomingCall, isResponding, stopRingtone, handleCallEnd]);

  // Deliberately NOT guarded by isResponding. If accept stalls, this is the
  // only way for the user to dismiss the dialog — the previous guard made it
  // unclosable once accept had been pressed.
  const rejectCall = useCallback(async () => {
    if (!incomingCall) return;

    stopRingtone();
    toast.dismiss();

    try {
      if (socket?.connected) {
        socket.emit('respond-to-call', {
          callId: incomingCall.callId,
          response: 'rejected'
        });
      }
    } catch (error) {
      // Dismiss locally regardless of whether the server heard us.
    }

    handleCallEnd('Call declined');
  }, [socket, incomingCall, stopRingtone, handleCallEnd]);

  useEffect(() => {
    mountedRef.current = true;
    if (loading) return;

    if (shouldHandleCalls()) {
      const timer = setTimeout(() => { connectSocket(); }, 1000);
      return () => clearTimeout(timer);
    }
  }, [loading, shouldHandleCalls, connectSocket]);

  useEffect(() => {
    // Copied into a local so the cleanup does not read a ref that may have
    // changed by the time it runs.
    const stop = stopRingtone;

    return () => {
      mountedRef.current = false;
      stop();
      toast.dismiss();
    };
  }, [stopRingtone]);

  if (!isAuthenticated || loading || !shouldHandleCalls()) {
    return null;
  }

  return (
    <div data-call-handler="true">
      {incomingCall && (
        <Dialog
          open={!!incomingCall}
          onClose={rejectCall}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }
          }}
        >
          <DialogContent sx={{ textAlign: 'center', py: 4, position: 'relative' }}>
            <IconButton
              sx={{ position: 'absolute', top: 16, right: 16, color: 'white' }}
              onClick={rejectCall}
              aria-label="Decline call"
            >
              <Close />
            </IconButton>

            <Avatar
              sx={{
                width: 80,
                height: 80,
                margin: '0 auto 16px',
                bgcolor: 'rgba(255,255,255,0.2)',
                fontSize: '2rem'
              }}
            >
              <Person fontSize="large" />
            </Avatar>

            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Incoming Video Call
            </Typography>

            <Typography variant="body1" sx={{ mb: 2, opacity: 0.9 }}>
              {incomingCall.caller?.name || 'Unknown Caller'}
            </Typography>

            <Chip
              icon={<Videocam />}
              label={incomingCall.bookingType === 'graphologist' ? 'Handwriting Analysis' : 'Video Call'}
              sx={{ background: 'rgba(255,255,255,0.2)', color: 'white', mb: 3 }}
            />
          </DialogContent>

          <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
            <Button
              variant="contained"
              color="error"
              startIcon={<PhoneDisabled />}
              onClick={rejectCall}
              disabled={isResponding}
              sx={{ borderRadius: '25px', px: 3, py: 1, minWidth: '120px' }}
            >
              Decline
            </Button>

            <Button
              variant="contained"
              color="success"
              startIcon={isResponding ? <CircularProgress size={20} /> : <Phone />}
              onClick={acceptCall}
              disabled={isResponding}
              sx={{
                borderRadius: '25px',
                px: 3,
                py: 1,
                background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                minWidth: '120px'
              }}
            >
              {isResponding ? 'Connecting…' : 'Accept'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

export default GlobalIncomingCallHandler;